"use client";
import { useState } from "react";
import styles from "./styles.module.scss";
import { useRouter } from "next/navigation";
import useIsMobile from "@app/hooks/useIsMobile";
import { useTranslation } from "@app/useTranslation";

import StyledIcon from "@generic-components/StyledIcon";
import { useSelector } from "react-redux";
import {
  getIsLoggedIn,
  getRole,
  getUserDetail,
} from "@app/lib/features/login/loginSlice";
import LoginPageComponent from "@app/components/page/Login";
import { getNotFound } from "@app/lib/features/not-found/notFoundSlice";
import { pushGaEvents } from "@app/utils/common";

interface SocialMediaProps {
  propertyName: string;
  propertyType: string;
  propertyPrice: string;
}

interface SocialMediaNavigationProps {
  icon: string;
  featureName: string;
  featureValue: string;
}

const socialMediaNavigation: SocialMediaNavigationProps[] = [
  { icon: "facebook", featureName: "facebook", featureValue: "4" },
  { icon: "telegram", featureName: "youtube", featureValue: "1" },
  { icon: "instagram", featureName: "instagram", featureValue: "4" },
];

const Footer = () => {
  const isNotFound = useSelector((state: any) => getNotFound(state));
  const route = useRouter();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [errEmail, setErrEmail] = useState("");
  const [showModal, setShowModal] = useState(false);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const userRole = useSelector(getRole);
  const userInfo = useSelector(getUserDetail);

  if (isNotFound) {
    return null;
  }

  const mobileFooterData = {
    pages: [
      {
        label: "Home",
        url: "/",
      },
      {
        label: "Buy with VYOM",
        url: "/buy",
      },
      {
        label: "Sell your property",
        url: "/sell",
      },
      {
        label: "Register",
        url: "",
      },
      {
        label: "Contact Us",
        url: "/contact-us",
      },
    ],
    policyPages: [
      {
        label: "Terms of Use",
        url: "/terms-and-conditions",
      },
      {
        label: "Privacy Policy",
        url: "/privacy-policy",
      },
      {
        label: "Cookie Policy",
        url: "/cookie-policy",
      },
      // {
      //   label: "Cookie Details",
      //   url: "/cookie-details",
      // },
      // {
      //   label: "Service Agreement",
      //   url: "/service-agreement",
      // },
    ],
  };

  const toggleSignUp = () => {
    setShowModal((prev) => !prev);
  };

  const handleFooterNavigationClick = (label: string, url: string) => {
    pushGaEvents({
      event: "footer_link_click",
      link_name: label,
      user_id: userInfo?.userDetails?.userId || "",
    });

    route.push(url);
  };

  const size = typeof window !== "undefined" ? window.innerWidth : 1024;

  return (
    <>
      {showModal && (
        <LoginPageComponent
          isSignUp={true}
          view={showModal}
          onClose={toggleSignUp}
        />
      )}
      {size < 1024 || isMobile ? (
        <div className={`${styles.bgCustom} ${styles.mobileFooter} `}>
          <div className={styles.mobileLogo}>
            <span
              className="cursor-pointer"
              onClick={() => {
                route.push("/");
              }}
            >
              <StyledIcon
                icon="logo"
                fill={"#00333E"}
                width={148}
                height={40}
              />
            </span>
          </div>
          <div className={styles.mobileText}>
            <p>
              VYOM By Emaar: Simplifying the buying and selling of Emaar
              properties for a smooth, seamless real estate experience.
            </p>
          </div>
          <div className={styles.linksWrapper}>
            {mobileFooterData?.pages?.map((item, index) => (
              <span
                key={index}
                className={`${styles.links}`}
                onClick={() => {
                  if (item?.label?.toLowerCase() === "register") {
                    toggleSignUp();
                  } else {
                    route.push(item?.url);
                  }
                }}
              >
                {item?.label}
              </span>
            ))}
          </div>
          <div className={`${styles.linksWrapper} my-5`}>
            {mobileFooterData?.policyPages?.map((item, index) => (
              <span
                key={index}
                className={styles.links}
                onClick={() => {
                  route.push(item?.url);
                }}
              >
                {item?.label}
              </span>
            ))}
          </div>
          <div className="mx-auto justify-items-center flex gap-5">
            {socialMediaNavigation.map((item, index) => (
              <div
                className="cursor-pointer"
                key={index}
                onClick={() => {
                  if (item.icon === "facebook") {
                    window.open(
                      "https://www.facebook.com/profile.php?id=61568547915496",
                      "_blank"
                    );
                  } else if (item.icon === "instagram") {
                    window.open(
                      "https://www.instagram.com/vyombyemaar/",
                      "_blank"
                    );
                  } else if (item.icon === "telegram") {
                    window.open(
                      "https://www.youtube.com/@VyomByEmaar",
                      "_blank"
                    );
                  }
                }}
              >
                <StyledIcon
                  // color="#100E2C"
                  icon={item.icon}
                  // inline={true}
                  width={50}
                  height={50}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div
          className={`${styles.bgCustom} text-dark lg:px-14 md:px-10 pt-8 md:pt-12 pb-2 `}
        >
          <div className="mx-auto flex justify-between md:m-0">
            <div className={`w-[32%]`}>
              <h5 className={`${styles.heading} mb-6`}>
                <StyledIcon
                  icon="logo"
                  fill={"#00333E"}
                  width={148}
                  height={40}
                />
              </h5>
              <div
                className={`${styles.description} w-full lg:w-[300px] xl:w-[400px]`}
              >
                VYOM By Emaar: Simplifying the buying and selling of Emaar
                properties for a smooth, seamless real estate experience.
              </div>
            </div>

            <div className={` col-span-2`}>
              <h5 className={`${styles.heading} mb-4`}>
                {t("footer.quickLink")}
              </h5>
              {/* <div className={styles.headingBottom}>^^^^^</div> */}
              <ul>
                <li
                  className={`${styles.links} cursor-pointer`}
                  onClick={() => handleFooterNavigationClick("Home", "/home")}
                >
                  <span> &gt; </span>
                  {t("footer.home")}
                </li>
                <li
                  className={`${styles.links} cursor-pointer`}
                  onClick={() => handleFooterNavigationClick("Buy With Vyom", "/buy")}
                >
                  <span> &gt; </span>
                  {t("footer.buyWithVyom")}
                </li>
                {((userRole === "seller" && isLoggedIn) || !isLoggedIn) && (
                  <li
                    className={`${styles.links} cursor-pointer`}
                    onClick={() => handleFooterNavigationClick("Sell Your Property", "/sell")}
                  >
                    <span> &gt; </span>
                    {t("footer.sellYourProperty")}
                  </li>
                )}
                {!isLoggedIn && (
                  <li
                    onClick={toggleSignUp}
                    className={`${styles.links} cursor-pointer`}
                  >
                    <span> &gt; </span>
                    {t("footer.register")}
                  </li>
                )}
                <li
                  className={`${styles.links} cursor-pointer`}
                  onClick={() => handleFooterNavigationClick("Contact Us", "/contact-us")}
                >
                  <span> &gt; </span>
                  {t("footer.contactUs")}
                </li>
              </ul>
            </div>

            <div className={` col-span-2`}>
              <h5 className={`${styles.heading} mb-4 text-transparent`}>
                {t(" .")}
              </h5>
              {/* <div className={styles.headingBottom}>^^^^^</div> */}
              <ul>
                <li
                  onClick={() => handleFooterNavigationClick("Terms And Conditions", "/terms-and-conditions")}
                  className={`${styles.links} cursor-pointer`}
                >
                  <span> &gt; </span>
                  {t("footer.tnc")}
                </li>
                <li
                  onClick={() => handleFooterNavigationClick("Privacy Policy", "/privacy-policy")}
                  className={`${styles.links} cursor-pointer`}
                >
                  <span> &gt; </span>
                  {t("footer.privacyPolicy")}
                </li>
                {/* <li
                  onClick={() => {
                    route.push("/service-agreement");
                  }}
                  className={`${styles.links} cursor-pointer`}
                >
                  <span> &gt; </span>
                  Service Agreement
                </li>
                <li
                  onClick={() => {
                    route.push("/cookie-details");
                  }}
                  className={`${styles.links} cursor-pointer`}
                >
                  <span> &gt; </span>
                  Cookie Details
                </li> */}
                <li
                  onClick={() => handleFooterNavigationClick("Cookie Policy", "/cookie-policy")}
                  className={`${styles.links} cursor-pointer`}
                >
                  <span> &gt; </span>
                  Cookie Policy
                </li>
              </ul>
            </div>
            <div className={`col-span-3 pr-2 xl:pr-10`}>
              <h5 className={`${styles.heading} mb-4`}>Social</h5>
              {/* <div className={styles.headingBottom}>^^^^^</div> */}
              <div className="flex relative -left-1.5 space-x-4 mt-4 inputDesign">
                {socialMediaNavigation.map((item, index) => (
                  <div
                    key={index}
                    className={`inputDesign cursor-pointer`}
                    onClick={() => {
                      if (item.icon === "facebook") {
                        window.open(
                          "https://www.facebook.com/profile.php?id=61568547915496",
                          "_blank"
                        );
                      } else if (item.icon === "instagram") {
                        window.open(
                          "https://www.instagram.com/vyombyemaar/",
                          "_blank"
                        );
                      } else if (item.icon === "telegram") {
                        window.open(
                          "https://www.youtube.com/@VyomByEmaar",
                          "_blank"
                        );
                      }
                    }}
                  >
                    <StyledIcon icon={item.icon} width={50} height={50} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      <div
        // style={{ backgroundImage: `url(${wave.src})` }}
        className="text-end h-24 pt-8 w-full flex items-center px-5"
      >
        <img
          alt="footer"
          src="https://veofitness.azureedge.net/wp-content/themes/bootscore-child-custom/img/icons/emaar-bottom-footer.svg"
          className="md:pl-8"
        />

        <span className={`font-light w-full ${styles.copyrightInfo}`}>
          {t("footer.copyrightInfo") +
            (isMobile ? "" : " All Rights Reserved.")}
        </span>
      </div>
    </>
  );
};

export default Footer;
